import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, Navigate, useLocation } from 'react-router-dom'
import { Button } from '../components/common/Button'
import { ConfirmModal } from '../components/common/Modal'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { FlashcardCard } from '../components/flashcards/FlashcardCard'
import { FlashcardCreateModal } from '../components/flashcards/FlashcardCreateModal'
import { useUserStore } from '../store/userStore'
import { useLanguageStore } from '../store/languageStore'
import type { Flashcard, MasteryLevel } from '../../../shared/types'
import api from '../services/api'
import {
  FLASHCARD_CACHE_TTL,
  buildFlashcardCacheKey,
  useFlashcardCacheStore,
} from '../store/flashcardCacheStore'
import type { FlashcardListResponse } from '../store/flashcardCacheStore'

type FlashcardWithInternalIds = Flashcard & {
  _docId?: string
  _originalCardId?: string | number
}

type LoadFlashcardsOptions = {
  silent?: boolean
}

const FLASHCARD_LIST_LIMIT = 100

const masteryCardConfigs: Record<
  MasteryLevel,
  { label: string; baseClasses: string; ringClass: string }
> = {
  new: {
    label: 'New',
    baseClasses: 'bg-blue-50 text-blue-700',
    ringClass: 'ring-blue-400',
  },
  learning: {
    label: 'Learning',
    baseClasses: 'bg-yellow-50 text-yellow-700',
    ringClass: 'ring-yellow-400',
  },
  review: {
    label: 'Review',
    baseClasses: 'bg-orange-50 text-orange-700',
    ringClass: 'ring-orange-400',
  },
  mastered: {
    label: 'Mastered',
    baseClasses: 'bg-green-50 text-green-700',
    ringClass: 'ring-green-400',
  },
}

const masteryCardOrder: MasteryLevel[] = ['new', 'learning', 'review', 'mastered']

const emptyBreakdown: Record<MasteryLevel, number> = {
  new: 0,
  learning: 0,
  review: 0,
  mastered: 0,
}

const normalizeBreakdown = (raw?: Record<string, number>): Record<MasteryLevel, number> => ({
  new: raw?.new ?? 0,
  learning: raw?.learning ?? 0,
  review: raw?.review ?? 0,
  mastered: raw?.mastered ?? 0,
})

const deriveFlashcardKey = (card: FlashcardWithInternalIds): string | null => {
  const docId = (card as any)?._docId
  if (typeof docId === 'string' && docId.trim().length > 0) {
    return `doc:${docId}`
  }

  if (typeof card.id === 'number' && Number.isFinite(card.id)) {
    return `id:${card.id}`
  }

  const original = (card as any)?._originalCardId
  if (original !== undefined && original !== null) {
    return `orig:${original}`
  }

  const fallbackParts = [
    card.targetText?.trim() || card.japaneseText?.trim(),
    card.sourceText?.trim() || card.englishTranslation?.trim(),
    card.nextReviewDate ?? '',
  ].filter(Boolean)

  return fallbackParts.length > 0 ? `text:${fallbackParts.join('|')}` : null
}

export function FlashcardList() {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useUserStore((state) => state.user)
  const currentLanguage = useLanguageStore((state) => state.currentLanguage)
  const availableLanguages = useLanguageStore((state) => state.availableLanguages)

  const [filterMastery, setFilterMastery] = useState<MasteryLevel | 'all'>('all')
  const initialCacheKey = user
    ? buildFlashcardCacheKey({
      userId: user.id.toString(),
      targetLanguage: currentLanguage || 'all',
      masteryLevel: filterMastery,
      limit: FLASHCARD_LIST_LIMIT,
    })
    : null
  const initialCacheEntry = initialCacheKey
    ? useFlashcardCacheStore.getState().getEntry(initialCacheKey)
    : undefined
  const [flashcards, setFlashcards] = useState<Flashcard[]>(() => initialCacheEntry?.data.flashcards ?? [])
  const [isLoading, setIsLoading] = useState(() => !initialCacheEntry)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(() => initialCacheEntry?.data.totalCount ?? 0)
  const [masteryBreakdown, setMasteryBreakdown] = useState<Record<MasteryLevel, number>>(
    () => (initialCacheEntry?.data ? normalizeBreakdown(initialCacheEntry.data.masteryBreakdown) : { ...emptyBreakdown }),
  )
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [cardPendingDeletion, setCardPendingDeletion] = useState<Flashcard | null>(null)
  const [reviewDirection, setReviewDirection] = useState<'jp-en' | 'en-jp' | 'mixed'>(() => {
    const saved = localStorage.getItem('reviewDirection')
    return (saved as 'jp-en' | 'en-jp' | 'mixed') || 'mixed'
  })
  const [audioOnlyMode, setAudioOnlyMode] = useState(() => {
    const saved = localStorage.getItem('audioOnlyMode')
    return saved === 'true'
  })
  const pendingDeleteKeysRef = useRef<Set<string>>(new Set())
  const loadRequestIdRef = useRef(0)
  const getCacheEntry = useFlashcardCacheStore((state) => state.getEntry)
  const setCacheEntry = useFlashcardCacheStore((state) => state.setEntry)

  const cacheKey = useMemo(() => {
    if (!user) return null
    return buildFlashcardCacheKey({
      userId: user.id.toString(),
      targetLanguage: currentLanguage || 'all',
      masteryLevel: filterMastery,
      limit: FLASHCARD_LIST_LIMIT,
    })
  }, [user, currentLanguage, filterMastery])

  const filterPendingDeletes = useCallback((cards: Flashcard[]): Flashcard[] => {
    if (pendingDeleteKeysRef.current.size === 0) {
      return cards
    }

    return cards.filter((card) => {
      const key = deriveFlashcardKey(card as FlashcardWithInternalIds)
      return !key || !pendingDeleteKeysRef.current.has(key)
    })
  }, [])

  // Save review preferences to localStorage
  useEffect(() => {
    localStorage.setItem('reviewDirection', reviewDirection)
  }, [reviewDirection])

  useEffect(() => {
    localStorage.setItem('audioOnlyMode', audioOnlyMode.toString())
  }, [audioOnlyMode])

  const targetLanguageLabel = useMemo(() => {
    const match = availableLanguages.find((lang) => lang.code === currentLanguage)
    return match?.name || (currentLanguage ? currentLanguage.toUpperCase() : 'JP')
  }, [availableLanguages, currentLanguage])

  const targetLanguageShort = useMemo(() => {
    return currentLanguage ? currentLanguage.toUpperCase() : 'JP'
  }, [currentLanguage])

  const applyFlashcardResponse = useCallback((data: FlashcardListResponse) => {
    setFlashcards(filterPendingDeletes(data.flashcards ?? []))
    setTotalCount(data.totalCount ?? data.flashcards?.length ?? 0)
    setMasteryBreakdown(normalizeBreakdown(data.masteryBreakdown))
  }, [filterPendingDeletes])

  useEffect(() => {
    if (!cacheKey) return
    const cached = getCacheEntry(cacheKey)
    if (cached) {
      applyFlashcardResponse(cached.data)
      setIsLoading(false)
    }
  }, [cacheKey, getCacheEntry, applyFlashcardResponse])

  const loadFlashcards = useCallback(async (options?: LoadFlashcardsOptions) => {
    if (!user || !cacheKey) return

    const isSilent = options?.silent === true
    const cacheEntry = getCacheEntry(cacheKey)
    const now = Date.now()

    if (cacheEntry) {
      applyFlashcardResponse(cacheEntry.data)
      if (now - cacheEntry.timestamp <= FLASHCARD_CACHE_TTL) {
        if (!isSilent) setIsLoading(false)
        return
      }
    } else if (!isSilent) {
      setIsLoading(true)
    }

    setError(null)
    const requestId = isSilent ? loadRequestIdRef.current : ++loadRequestIdRef.current

    try {
      const data = await api.getFlashcards(user.id.toString(), {
        limit: FLASHCARD_LIST_LIMIT,
        offset: 0,
        masteryLevel: filterMastery === 'all' ? undefined : filterMastery,
        targetLanguage: currentLanguage || undefined,
      })

      if (loadRequestIdRef.current !== requestId) {
        return
      }

      applyFlashcardResponse(data)
      setCacheEntry(cacheKey, data)
    } catch (err) {
      if (loadRequestIdRef.current !== requestId) {
        return
      }

      console.error('Load flashcards error:', err)
      if (!isSilent) {
        setError(err instanceof Error ? err.message : 'Failed to load flashcards')
      }
    } finally {
      if (!isSilent && loadRequestIdRef.current === requestId) {
        setIsLoading(false)
      }
    }
  }, [user, cacheKey, getCacheEntry, setCacheEntry, filterMastery, currentLanguage, applyFlashcardResponse])

  // Reload when navigating back to this page
  useEffect(() => {
    loadFlashcards()
  }, [location.key, loadFlashcards])

  // Refresh data when user returns to this page (e.g., after reviewing cards)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadFlashcards({ silent: true })
      }
    }

    const handleFocus = () => {
      loadFlashcards({ silent: true })
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [loadFlashcards])

  const handleFlashcardDelete = useCallback(
    async (flashcard: Flashcard) => {
      // eslint-disable-next-line no-console
      console.log('[FlashcardList] onDelete invoked for card', {
        id: flashcard.id,
        docId: (flashcard as any)?._docId,
      })

      const MAX_VALID_ID = 1000000000
      const pendingKey = deriveFlashcardKey(flashcard as FlashcardWithInternalIds)
      let pendingKeyAdded = false

      try {
        const docId = (flashcard as any)._docId as string | undefined

        if (pendingKey) {
          pendingDeleteKeysRef.current.add(pendingKey)
          pendingKeyAdded = true
        }
        const deletedCardMastery = flashcard.masteryLevel

        setFlashcards((prev) => {
          console.log('[FlashcardList] Before filter, count:', prev.length)
          console.log(
            '[FlashcardList] Deleting card with id:',
            flashcard.id,
            'docId:',
            (flashcard as any)?._docId
          )
          const filtered = prev.filter((c) => {
            const currentKey = deriveFlashcardKey(c as FlashcardWithInternalIds)
            const sameKey = pendingKey && currentKey ? currentKey === pendingKey : false
            const sameNumeric = c.id && c.id > 0 && c.id <= MAX_VALID_ID && c.id === flashcard.id
            const sameDoc =
              (c as any)?._docId && (c as any)?._docId === (flashcard as any)?._docId
            const shouldRemove = sameNumeric || sameDoc
            if (shouldRemove || sameKey) {
              console.log('[FlashcardList] Removing card:', c.id, (c as any)?._docId)
            }
            return !(shouldRemove || sameKey)
          })
          console.log('[FlashcardList] After filter, count:', filtered.length)
          return filtered
        })

        setTotalCount((prev) => Math.max(0, prev - 1))

        setMasteryBreakdown((prev) => ({
          ...prev,
          [deletedCardMastery]: Math.max(0, prev[deletedCardMastery] - 1),
        }))

        if (docId) {
          await api.deleteFlashcardByDoc(docId)
        } else if (flashcard.id && flashcard.id > 0 && flashcard.id <= MAX_VALID_ID) {
          await api.deleteFlashcard(flashcard.id)
        } else {
          throw new Error('Card identifier missing')
        }

        try {
          await loadFlashcards({ silent: true })
        } catch (reloadErr) {
          console.error('Silent reload after delete success failed:', reloadErr)
        }
      } catch (err) {
        console.error('Delete flashcard failed:', err)
        alert(err instanceof Error ? err.message : 'Failed to delete flashcard')
        try {
          await loadFlashcards({ silent: true })
        } catch (reloadErr) {
          console.error('Failed to reload flashcards after delete error:', reloadErr)
        }
      } finally {
        if (pendingKeyAdded && pendingKey) {
          pendingDeleteKeysRef.current.delete(pendingKey)
        }
        setCardPendingDeletion(null)
      }
    },
    [loadFlashcards]
  )

  // If no user, redirect immediately to profile to avoid loading spinner
  if (!user) {
    return <Navigate to="/profile" replace />
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="page-shell">
      {/* Header */}
      <div
        className="bg-white border-b border-gray-200 px-4 py-6"
      >
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-700 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Home
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Flashcards</h1>
              <p className="text-gray-600 mt-2">
                {totalCount} {totalCount === 1 ? 'card' : 'cards'} in your collection
              </p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              variant="primary"
              className="flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create Flashcard
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Mastery Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {masteryCardOrder.map((level) => {
            const config = masteryCardConfigs[level]
            const isActive = filterMastery === level
            return (
              <button
                type="button"
                key={level}
                onClick={() =>
                  setFilterMastery((current) => (current === level ? 'all' : level))
                }
                className={`rounded-lg p-4 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 cursor-pointer select-none ${config.baseClasses} ${isActive
                    ? `ring-2 ring-offset-2 ${config.ringClass}`
                    : 'hover:ring-2 hover:ring-offset-2 hover:ring-gray-200'
                  }`}
              >
                <div className="text-2xl font-bold">{masteryBreakdown[level]}</div>
                <div className="text-sm font-medium flex items-center gap-2">
                  {config.label}
                  {isActive && (
                    <span className="text-xs font-semibold bg-white/70 text-gray-900 px-2 py-0.5 rounded-full">
                      Filtering
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* Review Settings */}
        <div
          className="bg-white rounded-lg shadow-sm p-4 mb-6"
        >
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Review Settings</h3>

          {/* Review Direction */}
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Review Direction
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setReviewDirection('mixed')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition ${reviewDirection === 'mixed'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                Mixed ({targetLanguageShort} ⇄ EN)
              </button>
              <button
                onClick={() => setReviewDirection('jp-en')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition ${reviewDirection === 'jp-en'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {targetLanguageLabel} → English
              </button>
              <button
                onClick={() => setReviewDirection('en-jp')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition ${reviewDirection === 'en-jp'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                English → {targetLanguageLabel}
              </button>
            </div>
          </div>

          {/* Audio Only Mode */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 block">
                Audio Only Mode
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Listen and respond without seeing the text
              </p>
            </div>
            <button
              onClick={() => setAudioOnlyMode(!audioOnlyMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${audioOnlyMode ? 'bg-primary-600' : 'bg-gray-300'
                }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${audioOnlyMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-error-50 border border-error-200 text-error-700 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Flashcard Grid */}
        {flashcards.length === 0 ? (
          <div
            className="bg-white rounded-lg shadow-sm p-12 text-center"
          >
            <div className="text-6xl mb-4">📚</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No flashcards yet</h2>
            <p className="text-gray-600 mb-6">
              Flashcards will be automatically generated from your conversation practice.
            </p>
            <Button onClick={() => navigate('/conversation')} variant="primary">
              Start a Conversation
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {flashcards.map((flashcard) => {
              return (
                <div
                  key={
                    flashcard.id && flashcard.id > 0 && flashcard.id <= 1000000000
                      ? flashcard.id
                      : (flashcard as any)._docId
                  }
                  className="w-full"
                >
                  <FlashcardCard
                    flashcard={flashcard}
                    onDelete={() => setCardPendingDeletion(flashcard)}
                    usePremiumVoices={user?.subscriptionTier === 'premium'}
                    preferredVoiceId={user?.preferredVoiceId ?? null}
                  />
                </div>
              )
            })}
          </div>
        )}

        {/* Review Button */}
        {totalCount > 0 && (
          <div className="fixed bottom-6 right-6">
            <Button
              onClick={() => navigate('/flashcards/review', {
                state: {
                  reviewDirection,
                  audioOnlyMode,
                }
              })}
              variant="primary"
              size="lg"
              className="rounded-full shadow-lg"
            >
              Start Review
            </Button>
          </div>
        )}
      </div>

      {/* Create Flashcard Modal */}
      {user && (
        <FlashcardCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={loadFlashcards}
          userId={user.id}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(cardPendingDeletion)}
        onClose={() => setCardPendingDeletion(null)}
        onConfirm={async () => {
          if (cardPendingDeletion) {
            await handleFlashcardDelete(cardPendingDeletion)
          }
        }}
        title="Delete this flashcard?"
        message={`This will permanently remove "${cardPendingDeletion?.sourceText ||
          cardPendingDeletion?.englishTranslation ||
          cardPendingDeletion?.targetText ||
          cardPendingDeletion?.japaneseText ||
          'this flashcard'
          }". This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Keep Flashcard"
        variant="error"
      />
    </div>
  )
}

export default FlashcardList
